import { TariffResponseData } from '@/helpers/fetchTariffs/types';
import RegioChecklist from '@uikit/components/RegioChecklist/RegioChecklist';
import RegioContactSection from '@uikit/components/RegioContactSection/RegioContactSection';
import RegioFact from '@uikit/components/RegioFact/RegioFact';
import RegioStage from '@uikit/components/RegioStage/RegioStage';
import RegioTeaser from '@uikit/components/RegioTeaser/RegioTeaser';
import Section from '@uikit/components/Section/Section';
import Spacer from '@uikit/components/Spacer/Spacer';
import { formatNumber } from '@uikit/helpers/formatNumber';
import getCheapestTariff from '@uikit/helpers/getCheapestTariff';
import getTariffSavingsAmount from '@uikit/helpers/getTariffSavingsAmount';
import hasSufficientSavingsAmount from '@uikit/helpers/isTariffCheaperThanBaseProvider';
import { useCallback, useState } from 'react';
import { ClickableAction } from '../Clickable/consts';
import ComponentList from '../ComponentList/ComponentList';
import { JustifyContent } from '../ComponentList/consts';
import Container from '../Container/Container';
import { ContainerSize } from '../Container/consts';
import Headline from '../Headline/Headline';
import { HeadlineAlign, HeadlineColor } from '../Headline/consts';
import Link from '../Link/Link';
import RegioKeyVisualSection from '../RegioKeyVisualSection/RegioKeyVisualSection';
import RegioTabs from '../RegioTabs/RegioTabs';
import RegioTariffWrapper from '../RegioTariffWrapper/RegioTariffWrapper';
import Teaser from '../Teaser/Teaser';
import styles from './RegioPageContent.module.scss';
import {
  DEFAULT_IMAGES,
  PRODUCT_DETAILS,
  SAVINGS_AMOUNT_THRESHOLD,
  relatedLinks,
} from './consts';
import { createGermanAbsoluteLinks } from './helpers';
import type { RegioPageContentProps } from './types';

const RegioPageContentGas = ({
  baseProviderTariff,
  cityName,
  cityKey,
  headline,
  imageSustainability,
  lastUpdated,
  nearestCities,
  population,
  productType,
  subText,
  tariffs,
  fallbackTariffs,
  textSustainability,
  textSustainabilityEnercity,
  zipCode,
  streetName,
  streetNumber,
  regioProductPaths,
  configuratorUrl,
  titleSustainability,
  tippsHeadline,
  tippsSubText,
  tipp1,
  tipp2,
  tippHeadline1,
  tippHeadline2,
}: RegioPageContentProps) => {
  const [selectedConsumption, setSelectedConsumption] = useState<number>(
    PRODUCT_DETAILS[productType][0].consumption
  );
  const cheapestTariff = getCheapestTariff(tariffs);
  const savingsAmount = getTariffSavingsAmount(
    cheapestTariff,
    baseProviderTariff
  );
  const isCheaperThanBaseProvider = hasSufficientSavingsAmount(
    savingsAmount,
    SAVINGS_AMOUNT_THRESHOLD
  );

  const initialData: TariffResponseData = {
    [productType]: {
      tariffs: tariffs?.length ? tariffs : fallbackTariffs,
      baseProviderTariff,
    },
  };

  const regioStageSubtext = isCheaperThanBaseProvider
    ? subText
    : `enercity - Ihr Gasanbieter für ${cityName}.`;

  const tariffComparisonTitle = isCheaperThanBaseProvider
    ? 'Vertrauen ist gut - vergleichen ist besser!'
    : `Unsere Gastarife für ${cityName}`;

  const tariffComparisonSubtitle = isCheaperThanBaseProvider
    ? `Sparen Sie bares Geld in ${cityName}! In unserer Beispielrechnung zeigen wir Ihnen, was für Sie herausspringt.`
    : `Entdecken Sie die attraktiven Gastarife in Top Stadtwerke-Qualität für Ihr Zuhause!`;

  const handleChange = useCallback(
    (value: string) => {
      setSelectedConsumption(+value);
    },
    [setSelectedConsumption]
  );

  return (
    <div className={styles.base}>
      <Section>
        <Container>
          <RegioStage
            city={cityName}
            description={regioStageSubtext}
            headline={headline}
            configuratorUrl={configuratorUrl}
            stageBoxTitle={`Jetzt Gastarif für ${cityName} berechnen!`}
          />
        </Container>
      </Section>
      <Spacer defaultSize={2} sm={3} />
      <Section hasPadding={false} hasBottomSpace={false}>
        <Container size={ContainerSize.Max}>
          <Headline
            subText={tariffComparisonSubtitle}
            headlineColor={HeadlineColor.Gradient}
            className={styles.headline}
          >
            {tariffComparisonTitle}
          </Headline>
          <RegioTabs
            onValueChange={handleChange}
            defaultValue={PRODUCT_DETAILS[productType]
              ?.at(0)
              ?.consumption?.toString()}
            id="vergleich"
          >
            <RegioTabs.List>
              {PRODUCT_DETAILS[productType].map(
                ({ label, consumption, icon }) => (
                  <RegioTabs.Trigger
                    value={consumption?.toString()}
                    iconVariant={icon}
                    key={`navitem-${consumption}`}
                  >
                    <span className={styles.tabTitle}>
                      {label}
                      <span className={styles.consumption}>
                        ({`${formatNumber(consumption)} kWh`})
                      </span>
                    </span>
                  </RegioTabs.Trigger>
                )
              )}
            </RegioTabs.List>
            {PRODUCT_DETAILS[productType].map(({ consumption }) => {
              return (
                <RegioTabs.Content
                  value={consumption.toString()}
                  key={`content-${consumption}`}
                >
                  <RegioTariffWrapper
                    city={cityName}
                    cityKey={cityKey}
                    configuratorUrl={configuratorUrl}
                    consumption={selectedConsumption}
                    lastUpdated={lastUpdated}
                    productType={productType}
                    streetName={streetName}
                    streetNumber={streetNumber}
                    fallbackTariffs={fallbackTariffs}
                    zipCode={zipCode}
                    staticData={initialData}
                  />
                </RegioTabs.Content>
              );
            })}
          </RegioTabs>
        </Container>
      </Section>
      <RegioKeyVisualSection
        image={imageSustainability}
        title={
          titleSustainability ||
          `Mit enercity Gas Klimaschutzprojekte fördern - auch in ${cityName}`
        }
        badgeText={undefined}
        mainText={
          textSustainability ||
          `Zur Erklärung: Erdgas ist eine vergleichsweise umweltschonende Art der Energiegewinnung. Trotzdem entsteht bei der Verbrennung von Erdgas eine gewisse Menge an CO2. Mit unseren enercity Gas Laufzeitprodukten tragen wir dazu bei, dass die bei der Erdgas-Verbrennung entstandene Menge CO2 zu 100 % ausgeglichen wird. Wie machen wir das? Durch den Kauf von Klimaschutzzertifikaten renommierter Anbieter, deren Projekte zu einer Verringerung der CO2-Belastung beitragen. So können wir gemeinsam unser Klima in ${cityName} schützen.`
        }
        highlightedText={
          textSustainabilityEnercity ||
          `Jetzt einfach Gas in ${cityName} anmelden. Mit Sicherheit gut versorgt - wie Sie es von Stadtwerken gewohnt sind.`
        }
        link={configuratorUrl}
        linkText="Jetzt wechseln"
        id="klimaschutz"
      />
      <Section sectionId="tipps">
        <Container className={styles.regioComparisonTipsContainer}>
          <Headline
            align={HeadlineAlign.Left}
            subText={
              tippsSubText ||
              `Es gibt in ${cityName} unzählige <strong>Gasanbieter</strong>${
                baseProviderTariff
                  ? ` und natürlich auch einen
          örtlichen <strong>Grundversorger - ${baseProviderTariff.providerName}</strong>`
                  : ''
              }. Wenn Sie das Gefühl
          haben, bei der Recherche auf <strong>Vergleichsportalen</strong> den Überblick über
          aktuelle Gaspreise und diverse <strong>Vergleichskriterien</strong> zu verlieren,
          haben wir zwei Tipps für Sie:`
            }
          >
            {tippsHeadline || `Gasvergleich für ${cityName}`}
          </Headline>
          <ComponentList justifyChild={JustifyContent.Stretch}>
            <Teaser
              headline={tippHeadline1 || 'Tipp 1'}
              icon="user/information"
              actionType={ClickableAction.None}
              headlineTag="div"
              isIcon
            >
              {tipp1 ||
                `Vergleichen Sie immer den reinen Grund- & Arbeitspreis für Ihren
              neuen Gastarif. Multiplizieren Sie den Arbeitspreis mit Ihrem
              Jahresverbrauch und addieren Sie den Grundpreis pro Jahr hinzu. So
              erhalten Sie Ihren Jahrespreis und können die Kosten pro Jahr bei
              verschiedenen Anbietern gegenüberstellen.`}
            </Teaser>
            <Teaser
              headline={tippHeadline2 || 'Tipp 2'}
              icon="user/information"
              actionType={ClickableAction.None}
              headlineTag="div"
              isIcon
            >
              {tipp2 ||
                `Bei einem möglichen Bonus gilt es folgendes zu beachten: Überprüfen Sie die Bedingungen für die Bonusauszahlung genau. Es ist möglich, dass ein Bonus für Ihre Gasanmeldung erst nach einer Mindestvertragslaufzeit gewährt wird und Ihr Gasvertrag in ${cityName} im Zeitvergleich gesehen doch teurer wird als gedacht.`}
            </Teaser>
          </ComponentList>
        </Container>
      </Section>
      <Section>
        <Container className={styles.regioFirstFactContainer}>
          <RegioFact>
            Mit geballter <strong>Energie</strong> für alle{' '}
            <strong>{population}</strong> Bürger:innen von{' '}
            <strong>{cityName}</strong> vor Ort.
          </RegioFact>
        </Container>
      </Section>
      <Section>
        <Container className={styles.regioTeaserContainer}>
          <RegioTeaser
            image={DEFAULT_IMAGES.TEASER_MORE_INFO}
            headline="enercity - bundesweit verfügbar"
            description={`Als einer der zehn größten Energieversorger Deutschlands versorgen wir Privat- und Gewerbekunden in ${cityName} <strong>als zuverlässiger Gasanbieter. Unser Versprechen an Sie: Zuverlässig, freundlich und kompetent.</strong> Entstanden aus den Stadtwerken Hannover versorgen wir unsere Heimat bereits seit über 125 Jahren mit Energie.`}
            link={`/privatkunden/produkte/gas`}
            linkText="Jetzt Gastarife von enercity entdecken"
          />
          <RegioTeaser
            image={DEFAULT_IMAGES.TEASER_CONTACT}
            headline={`Immer erreichbar - auch in ${cityName}`}
            description={`Ihre Bedürfnisse liegen uns am Herzen - deshalb sind wir jederzeit für Sie da. Sie erreichen uns per <strong>Chat, Telefon oder Mail</strong>. Jederzeit für Sie in ${cityName}.`}
            link="https://support.enercity.de"
            linkText="Kontaktieren Sie uns!"
          />
        </Container>
      </Section>
      <Section sectionId="checkliste">
        <Container>
          <RegioChecklist
            image={DEFAULT_IMAGES.CHECKLIST}
            mainTitle="An alles gedacht? Die Checkliste für Ihren Wechsel"
            mainDescription={`Sie wollen vor Ihrem Gasanbieterwechsel in ${cityName} noch die Preise vergleichen? Gute Idee. Wir zeigen Ihnen, worauf Sie bei der Wahl eines neuen Gasanbieters achten sollten.`}
            boxTitle="Alles klar soweit?"
            boxDescription="Sind alle Fragen beantwortet? Prima. Dann steht einem Wechsel zu enercity - Ihrem neuen Gasanbieter - nichts mehr im Wege."
            listItems={[
              'Falls Ihnen stabile Preise wichtig sind: Achten Sie auf die Preisgarantie.',
              'Vergleichen Sie den Grund- bzw. Arbeitspreis Ihrer bevorzugten Lieferanten',
              'Ist der Anbieter neu am Markt oder schon lange etabliert?',
              'Sparfüchse aufgepasst: mit Boni und Prämien sparen Sie bares Geld!',
            ]}
            link={configuratorUrl}
            linkText="Jetzt wechseln"
          />
        </Container>
      </Section>

      <Section>
        <Container className={styles.regioSecondFactContainer}>
          <RegioFact>
            Das ist <strong>enercity - 125 Jahre</strong> Erfahrung.{' '}
            <strong>350.000</strong> zufriedene Kund:innen.
            <br />
          </RegioFact>
        </Container>
      </Section>
      <RegioContactSection title={`Wir sind für Sie in ${cityName} da!`}>
        <RegioContactSection.RichText>
          Unsere günstigen Gastarife finden Sie nicht nur in {cityName}, sondern
          auch in {createGermanAbsoluteLinks(nearestCities)}, sowie in vielen
          weiteren Städten.
        </RegioContactSection.RichText>
        <RegioContactSection.RichText>
          Neben grünem und günstigem Gas bieten wir als lokaler Gasanbieter
          bspw. auch {relatedLinks.power}, {relatedLinks.solar},{' '}
          {relatedLinks.emob}, {relatedLinks.heat}. Selbstverständlich können
          Sie bei enercity auch direkt den passenden{' '}
          <Link to={regioProductPaths.power}>Stromtarif für {cityName}</Link>{' '}
          abschließen.
        </RegioContactSection.RichText>
      </RegioContactSection>
    </div>
  );
};

export default RegioPageContentGas;
